/**
 * CroSupplyBreakdown — V2.K.1
 *
 * Renders a 3-way breakdown of CRO supply on the Cronos Monitor page :
 *
 *   ┌─────────────────────────────────────────────┐
 *   │  Circulating (free float) │ Locked │  ▒▒▒    │
 *   └─────────────────────────────────────────────┘
 *     ~43.6 B (44 %)            │ ~55 B  │ to mint
 *
 * Why this matters : the Cronos whitepaper caps supply at 100 B, but
 * CIP-006 (March 2025 vote) re-minted 70 B CRO into a Strategic
 * Reserve escrow that vests over 10 years (~1.167 B / month). So the
 * Total Supply will reach the 100 B cap around ~July 2027 — but the
 * Circulating Supply will keep growing until ~2035 as the escrow
 * unlocks. This component makes that distinction visible.
 *
 * Identified-wallet section : we list the very few wallets we can
 * verify from public sources (validators, one labelled Crypto.com hot
 * wallet, the standard burn address). Anything unverified is excluded
 * to avoid spreading misinformation.
 */

import { useTranslation } from 'react-i18next';
import { useCroSupply } from '../../hooks/useCroSupply';

const ANNUAL_UNLOCK_HBAR = 14_000_000_000; // 1.167 B/mo × 12 — Strategic Reserve CIP-006

// Known Cronos addresses — only entries we can verify from public,
// reputable sources. Each row carries a `verified` note so the user
// understands provenance.
const KNOWN_ADDRESSES: Array<{
  label: string;
  address: string;
  chain: 'evm' | 'pos';
  category: 'crypto-com' | 'burn' | 'validator';
  note: string;
}> = [
  {
    label: 'Crypto.com — CDC Withdrawal',
    address: '0xc9219731adfa70645be14cd5d30507266f2092c5',
    chain: 'evm',
    category: 'crypto-com',
    note: 'Public Cronoscan label — exchange withdrawal wallet.',
  },
  {
    label: 'Burn address (standard EVM)',
    address: '0x000000000000000000000000000000000000dEaD',
    chain: 'evm',
    category: 'burn',
    note: 'Universal EVM null address used for token burns on Cronos.',
  },
  {
    label: 'Falcon Heavy validator',
    address: 'crocncl139nl5fnhlxu2asduu5zqq8zzev0632jlf3lgam',
    chain: 'pos',
    category: 'validator',
    note: 'Crypto.com-operated validator on Cronos POS — adresse via staking.cafe (source secondaire). Non confirmée par une page officielle Crypto.com / Cronos Labs ; à recouper sur l\'explorer cronos-pos.org si besoin de garantie cryptographique.',
  },
];

// 5 Crypto.com-operated validators on Cronos POS — addresses for the
// 4 others are not publicly cataloged in a fully verifiable way, so
// we surface the names only.
const CRYPTO_COM_VALIDATORS = [
  'Starship',
  'Falcon Heavy',
  'Electron',
  'Antares',
  'Minotaur IV',
];

export function CroSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useCroSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;

  return (
    <section
      aria-labelledby="cro-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="cro-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#8259EF' }}>◉</span>
          {t('cronos.supply.title', 'Supply CRO — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'cronos.supply.subtitle',
            'Ce qui circule, ce qui est verrouillé, et ce qu\'il reste à émettre — sources CoinGecko + whitepaper Cronos.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <StackedBar data={data} t={t} />
          <Legend t={t} />
          <DilutionNote t={t} fmt={fmt} annualUnlock={ANNUAL_UNLOCK_HBAR} />
          <KnownAddresses t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useCroSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtB, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('cronos.supply.circulating', 'En circulation')}
        value={fmtB(data.circulating)}
        sub={`${fmt(data.circulating)} CRO · ${data.floatPct.toFixed(1)} %`}
        color="#10b981"
      />
      <StatCard
        label={t('cronos.supply.locked', 'Verrouillé')}
        value={fmtB(data.locked)}
        sub={`${fmt(data.locked)} CRO · ${data.lockedPct.toFixed(1)} %`}
        color="#f59e0b"
      />
      <StatCard
        label={t('cronos.supply.total', 'Supply totale')}
        value={fmtB(data.total)}
        sub={`${data.mintedPct.toFixed(2)} % du cap`}
        color="#8259EF"
      />
      <StatCard
        label={t('cronos.supply.max', 'Cap maximal')}
        value={fmtB(data.max)}
        sub={t('cronos.supply.maxNote', 'Plafond whitepaper')}
        color="#6366f1"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stacked bar (Circulating / Locked / To mint)
// ─────────────────────────────────────────────────────────────────────

function StackedBar({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useCroSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  const circulatingPct = (data.circulating / data.max) * 100;
  const lockedPct = (data.locked / data.max) * 100;
  const toMintPct = Math.max(0, 100 - circulatingPct - lockedPct);

  return (
    <div
      style={{
        marginBottom: '8px',
      }}
      aria-label={t('cronos.supply.barAlt', 'Répartition de la supply CRO sur le cap maximal de 100 milliards')}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          height: '24px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: `${circulatingPct}%`,
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            minWidth: circulatingPct > 5 ? 'auto' : '0',
            transition: 'width 0.5s ease',
          }}
          title={`${circulatingPct.toFixed(1)} %`}
        >
          {circulatingPct > 8 ? `${circulatingPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${lockedPct}%`,
            background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${lockedPct.toFixed(1)} %`}
        >
          {lockedPct > 8 ? `${lockedPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${toMintPct}%`,
            background:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05) 6px, rgba(255,255,255,0.10) 6px, rgba(255,255,255,0.10) 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
            fontSize: '10px',
            fontWeight: 600,
            transition: 'width 0.5s ease',
          }}
          title={`${toMintPct.toFixed(1)} %`}
        >
          {toMintPct > 8 ? `${toMintPct.toFixed(1)} %` : ''}
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#10b981" label={t('cronos.supply.legendCirc', 'En circulation (free float)')} />
      <LegendItem color="#f59e0b" label={t('cronos.supply.legendLocked', 'Verrouillé (Strategic Reserve + autres)')} />
      <LegendItem
        color="rgba(255,255,255,0.15)"
        label={t('cronos.supply.legendToMint', 'Reste à mint avant cap 100 Md')}
      />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Dilution note
// ─────────────────────────────────────────────────────────────────────

function DilutionNote({
  t,
  fmt,
  annualUnlock,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  annualUnlock: number;
}) {
  const monthly = annualUnlock / 12;
  const body = t(
    'cronos.supply.dilutionBody',
    "L'escrow Strategic Reserve (vote CIP-006 mars 2025) libère ~{{monthly}} CRO / mois pendant 5 ans (60 mois × ~1,167 Md = 70 Md, soit mai 2025 → ~mai 2030 selon la spec officielle GitHub), soit ~{{annual}} CRO / an. Des sources secondaires évoquent un total de 10 ans en incluant le lockup historique depuis l\'issuance CRO originale sur Ethereum — la spec officielle ne mentionne que les 5 ans post-V5 upgrade. La Total Supply atteindra le cap 100 Md fin T2 2027, mais la Circulating Supply continuera de croître jusqu\'à l\'épuisement de l\'escrow.",
  )
    .replace('{{monthly}}', fmt(monthly))
    .replace('{{annual}}', fmt(annualUnlock));
  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(245, 158, 11, 0.06)',
        border: '1px dashed rgba(245, 158, 11, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#f59e0b' }}>
        {t('cronos.supply.dilutionTitle', 'Dilution attendue — CIP-006')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'cronos.supply.dilutionSource',
          'Mécanique : Cosmos SDK PeriodicVestingAccount sur Cronos POS. Sources : blog.cronos.org / GitHub crypto-org-chain.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known addresses block
// ─────────────────────────────────────────────────────────────────────

function KnownAddresses({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('cronos.supply.knownAddressesTitle', 'Wallets identifiés sur Cronos')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'cronos.supply.knownAddressesDisclaimer',
          'Liste non exhaustive — uniquement les adresses publiquement labellisées ou vérifiables. L\'adresse exacte de l\'escrow Strategic Reserve (`cro1…`) reste un placeholder dans la proposition CIP-006 GitHub officielle et n\'a pas été retrouvée labellisée sur Mintscan. Les adresses operator des 4 autres validators Crypto.com (Starship, Electron, Antares, Minotaur IV) ne sont pas listées de manière fiable dans une source primaire — chercher via cronos-pos.org/explorer/validators par moniker.',
        )}
      </div>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '10px 0 0',
        }}
      >
        {KNOWN_ADDRESSES.map((entry) => (
          <li
            key={entry.address}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <CategoryPill category={entry.category} />
              <strong style={{ color: 'var(--text)' }}>{entry.label}</strong>
              <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
                ({entry.chain === 'evm' ? 'Cronos EVM' : 'Cronos POS'})
              </span>
            </div>
            <code
              style={{
                display: 'block',
                fontSize: '11px',
                color: 'var(--muted)',
                marginTop: '4px',
                wordBreak: 'break-all',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {entry.address}
            </code>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>

      <div
        style={{
          marginTop: '12px',
          paddingTop: '10px',
          borderTop: '1px solid var(--border)',
          fontSize: '11px',
          color: 'var(--muted)',
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: 'var(--text)' }}>
          {t('cronos.supply.validatorsTitle', 'Validators opérés par Crypto.com')}
        </strong>{' '}
        : {CRYPTO_COM_VALIDATORS.join(', ')}.{' '}
        {t(
          'cronos.supply.validatorsNote',
          'Contrôlent collectivement 70–80 % du voting power du réseau (source : couverture presse du vote CIP-006).',
        )}
      </div>
    </details>
  );
}

function CategoryPill({ category }: { category: 'crypto-com' | 'burn' | 'validator' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    'crypto-com': { bg: 'rgba(130, 89, 239, 0.15)', color: '#a78bfa', label: 'Crypto.com' },
    burn: { bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171', label: 'Burn' },
    validator: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Validator' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply CRO sera affichée dès que l'API répondra.
    </div>
  );
}
